import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import { useCreateNewHabitMutation } from "./habitsApiSlice"
import { PlusIcon, MinusIcon} from '@heroicons/react/24/outline'

const EtCreateHabit = ({setActiveAdminView}) => {
  // First Created - 26 Jun 2025
  // Author - Atul Pandey
  // Inherited from impactTypes and repurposed
  // Updated - 27 Jun 2025 - added timeBasedInstructions

  // 0 - Set Page Title
  useTitle('Create Habit')
  
  // 1 - set page navigations
  const navigate = useNavigate()

  // 2 - set the mutation to create the new section data
  const[createNewHabit, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewHabitMutation()

// 3 - create the form data object
const [formData, setFormData] = useState({
  habitName: "",
  habitDesc: "",
  habitPlexus: "Physical",
  habitTimeSegments: ["Morning"],
  habitReadinessArea: ["Personal Readiness"],
  habitFocusArea: ["PR: Strengthen Exec Presence"],
  habitInstructions: [],
  timeBasedInstructions: [                              // 27 Jun 2025
    {
      earlyMorning: [],
      morning: [],
      midDay: [],
      evening: [],
      night: [],
    },
  ],
  aboutMe: "",
  pic: "",
})

// 4 - Reset form upon successful submission
useEffect(() => {
  if(isSuccess) {
    setFormData({
      habitName: "",
      habitDesc: "",
      habitPlexus: "",
      habitTimeSegments: [],
      habitReadinessArea: [],
      habitFocusArea: [],
      habitInstructions: [],
      timeBasedInstructions: [                              // 27 Jun 2025
        {
          earlyMorning: [],
          morning: [],
          midDay: [],
          evening: [],
          night: [],
        },
      ],
      aboutMe: "",
      pic: "",
    })
    setActiveAdminView('habits'); 
  }

},[isSuccess])

// 5a - Form field updates / change handling
const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
}

// 5b - Generic handlers
const handleAdd = (fieldName, newItem) => {
  setFormData(prev => ({
    ...prev,
    [fieldName]: [...prev[fieldName], newItem]
  }))
}

const handleRemove = (fieldName, index) => {
  setFormData(prev => ({
    ...prev,
    [fieldName]: prev[fieldName].filter((_, i) => i !== index)
  }))
}

const handleFieldChange = (fieldName, index, value) => {
  const updated = formData[fieldName].map((item, i) => i === index ? value : item)
  setFormData(prev => ({ ...prev, [fieldName]: updated }))
}

// 5c - Handle Instructions
const handleAddInstruction = () => handleAdd("habitInstructions", "")
const handleRemoveInstruction = (index) => handleRemove("habitInstructions", index)
const handleInstructionChange = (index, value) => handleFieldChange("habitInstructions", index, value)

// 5d - Handle Time Based Instructions - 27 Jun 2025
const handleAddTimeInstruction1 = (segment) => {
  setFormData((prev) => {
    const copy = { ...prev };
    const current = copy.timeBasedInstructions[0][segment] || [];
    copy.timeBasedInstructions[0][segment] = [
      ...current,
      { instruction: "", createdAt: new Date(), updatedAt: new Date() },
    ];
    return copy;
  });
};

const handleAddTimeInstruction = (segment) => {
  setFormData((prev) => {
    const copy = JSON.parse(JSON.stringify(prev)); // deep clone safely

    copy.timeBasedInstructions[0][segment] = [
      ...(copy.timeBasedInstructions[0][segment] || []),
      { instruction: "", createdAt: new Date(), updatedAt: new Date() },
    ];

    return copy;
  });
};

const handleRemoveTimeInstruction = (segment, index) => {
  setFormData((prev) => {
    const copy = { ...prev };
    copy.timeBasedInstructions[0][segment].splice(index, 1);
    return { ...copy };
  });
};

const handleTimeInstructionChange = (segment, index, value) => {
  setFormData((prev) => {
    const copy = { ...prev };
    copy.timeBasedInstructions[0][segment][index].instruction = value;
    copy.timeBasedInstructions[0][segment][index].updatedAt = new Date();
    return copy;
  });
};

// 6 - function to strip html tags from sections
function stripHtml(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
}

// 7 - Form Submit Logic
const handleSubmit = async (e) => {
e.preventDefault()

// Initialize an object to collect the form data
const habitData = {
  habitName: formData.habitName,
  habitDesc: formData.habitDesc,
  habitPlexus: formData.habitPlexus,
  habitTimeSegments: formData.habitTimeSegments,
  habitReadinessArea: formData.habitReadinessArea,
  habitFocusArea: formData.habitFocusArea,
  habitInstructions: formData.habitInstructions,
  timeBasedInstructions: formData.timeBasedInstructions, // 27 Jun 2025
  aboutMe: formData.aboutMe,
}

// console.log('habit Name:',habitData.habitName)

if (formData.pic) {
  // Convert the image to a base64 string
  const reader = new FileReader()
  reader.onloadend = async () => {
    // Once conversion is done, add the base64 string to your data object
    habitData.pic = reader.result

    // Now, send the data object to the server
    try {
      console.log("Form submitted.")
      await createNewHabit(habitData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
  reader.readAsDataURL(formData.pic)
} else {
  // No image, send other data as is
  try {
    console.log("Form submitted.")
    await createNewHabit(habitData).unwrap() // Dispatch the RTK Query mutation
  } catch (error) {
    console.error(error)
  }
}
}

// 8 - Handle Cancel

const handleCancel = () => {
  setActiveAdminView('habits'); 
}

// 9 - Handle Clear
const handleClear = () => {
  setFormData({
    habitName: "",
      habitDesc: "",
      habitPlexus: "",
      habitTimeSegments: [],
      habitReadinessArea: [],
      habitFocusArea: [],
      habitInstructions: [],
      timeBasedInstructions: [                              // 27 Jun 2025
        {
          earlyMorning: [],
          morning: [],
          midDay: [],
          evening: [],
          night: [],
        },
      ],
      aboutMe: "",
      pic: "",
    })
}
return (
  <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
    {isLoading && <PulseLoader color={"#ea580c"} />}
    {isError && (
      <p className="text-orange-700 font-semi-bold py-2">
        {error?.data?.message}
      </p>
    )}
    <div className="bg-white p-1 m-1 rounded">
      <p className="p-1 m-1 font-bold font-poppins text-xl">Add a Habit</p>

      <div className="bg-gray-200 rounded m-1 p-1">
        <form
          onSubmit={handleSubmit}
          className="m-1 p-1 text-xs font-poppins"
        >
          
          <div id="habitName">
            <input
              type="text"
              name="habitName"
              placeholder="Enter the habit name here"
              value={formData.habitName}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLightWithPlace
              value={formData.habitDesc}
              handleChange={(content) =>
                setFormData({ ...formData, habitDesc: content })
              }
              placeholderText={
                "Write this habit is about?"
              }
              // toolbarId={'sectionDesc'}
            />
          </div>

          {/* Habit Instuctions */}
          <div id="habitInstructions" className="m-1 py-1 border border-white rounded">
            <p className="font-bold mx-2 px-2 mt-2">Habit Instructions</p>
            <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">Steps or guidance to perform this habit</p>

            {formData.habitInstructions.map((instruction, index) => (
              <div key={index} className="flex justify-start items-center mx-1 ">
                <input
                  type="text"
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder="Enter an instruction"
                  className="w-full rounded p-2 m-1 text-xs"
                />
                <div className="rounded bg-red-500 m-1 p-1">
                  <button type="button" onClick={() => handleRemoveInstruction(index)}>
                    <MinusIcon className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
            ))}

            <div className="flex justify-start items-center m-1">
              <p className="font-bold text-xs m-1 p-1">Add</p>
              <button type="button" onClick={handleAddInstruction}>
                <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
              </button>
            </div>
          </div>

         {/* Time-Based Instructions - 27 Jun 2025 */}
          <div id="timeBasedInstructions" className="m-1 py-1 bg-gray-100 border border-white rounded">
            <p className="font-bold mx-2 px-2 mt-2">Time-Based Instructions</p>
            <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">Steps or guidance per time segment</p>

            {["earlyMorning", "morning", "midDay", "evening", "night"].map((segment) => (
              <div key={segment} className="my-2 bg-gray-200 border border-gray-300 rounded m-2 p-2">
                <p className="font-bold text-sm capitalize mx-1 mb-1">{segment.replace(/([A-Z])/g, " $1")}</p>

                {(formData.timeBasedInstructions?.[0]?.[segment] || [])
                  .filter(instruction => !instruction.delete)
                  .map((item, index) => (
                    <div key={index} className="flex justify-start items-center mx-1">
                      <input
                        type="text"
                        value={item.instruction}
                        onChange={(e) => handleTimeInstructionChange(segment, index, e.target.value)}
                        placeholder={`Instruction for ${segment}`}
                        className="w-full rounded p-2 m-1 text-xs"
                      />
                      <div className="rounded bg-red-500 m-1 p-1">
                        <button type="button" onClick={() => handleRemoveTimeInstruction(segment, index)}>
                          <MinusIcon className="h-5 w-5 text-white" />
                        </button>
                      </div>
                    </div>
                  ))}

                <div className="flex justify-start items-center m-1">
                  <p className="font-bold text-xs m-1 p-1">Add</p>
                  <button type="button" onClick={() => handleAddTimeInstruction(segment)}>
                    <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>



          {/* Plexus Dropdown */}
          <div className="m-1 p-1">
            <label className="block font-semibold">Energy Plexus</label>
          <select
            name="habitPlexus"
            value={formData.habitPlexus}
            onChange={handleChange}
            className="w-full rounded p-2 m-1"
          >
            {["Physical", "Mental", "Emotional", "Intellectual", "Spiritual"].map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          </div>

          {/* Time Segments Checkboxes */}
          <div className="p-2 m-1 bg-white rounded">
            <p className="m-1 p-1 font-bold border-b">Select one or more time periods for this habit</p>
            <div className=" mt-2">
            {["Early Morning", "Morning", "Mid Day", "Evening", "Night"].map((segment) => (
              <label key={segment} className="mr-2">
                <input
                  type="checkbox"
                  checked={formData.habitTimeSegments.includes(segment)}
                  onChange={() => {
                    setFormData(prev => {
                      const exists = prev.habitTimeSegments.includes(segment);
                      const updated = exists
                        ? prev.habitTimeSegments.filter(s => s !== segment)
                        : [...prev.habitTimeSegments, segment];
                      return { ...prev, habitTimeSegments: updated };
                    });
                  }}
                />
                <span className="ml-1">{segment}</span>
              </label>
            ))}
            </div>
          </div>

          {/* Readiness Area Dropdown */}
          {/* <select
            name="habitReadinessArea"
            value={formData.habitReadinessArea}
            onChange={handleChange}
            className="w-full rounded p-2 m-1"
          >
            {["Personal Readiness", "Contextual Readiness", "Capabilities Readiness", "Leadership Readiness"].map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select> */}

          {/* Readiness Area Multi-Select Checkboxes  - 26 Jun 2025*/}
          <div className="p-2 m-1 bg-white rounded">
            <p className="m-1 p-1 font-bold border-b">Select one or more Readiness Areas this habit will impact</p>
            {[
              "Personal Readiness",
                "Contextual Readiness",
                "Capabilities Readiness",
                "Leadership Readiness"
            ].map((area) => (
              <label key={area} className="block ml-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.habitReadinessArea.includes(area)}
                  onChange={() => {
                    setFormData(prev => {
                      const exists = prev.habitReadinessArea.includes(area);
                      const updated = exists
                        ? prev.habitReadinessArea.filter(a => a !== area)
                        : [...prev.habitReadinessArea, area];
                      return { ...prev, habitReadinessArea: updated };
                    });
                  }}
                />
                <span className="ml-2">{area}</span>
              </label>
            ))}
          </div>

          {/* Focus Area Multi-Select Checkboxes */}
          <div className="p-2 m-1 bg-white rounded">
            <p className="m-1 p-1 font-bold border-b">Select one or more Change Areas this habit will impact</p>
            {[
              "PR: Strengthen Exec Presence", 
              "PR: Strengthen Energy", 
              "PR: Strengthen Self Awareness",
              "PR: Strengthen CORE VALUEs",
              "PR: Strengthen PURPOSE",
              "PR: Strengthen Thought Harnessing", 
              "PR: Strengthen Capabilties", 
              "PR: Strengthen Dispassion", 
              "PR: Strengthen WILL",
              "PR: Strengthen HABITs",
              "CR: Deepen Relationships", 
              "CR: Manage Reactions", 
              "CR: Manage Expectations",
              "CR: Align PURPOSE",
              "CR: Strengthen Influence",
              "CR: Handling Praise", 
              "CR: Handling Criticism", 
              "CR: Handling Success", 
              "CR: Handling Failures",
              "CR: Handling Distractions",
            ].map((area) => (
              <label key={area} className="block ml-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.habitFocusArea.includes(area)}
                  onChange={() => {
                    setFormData(prev => {
                      const exists = prev.habitFocusArea.includes(area);
                      const updated = exists
                        ? prev.habitFocusArea.filter(a => a !== area)
                        : [...prev.habitFocusArea, area];
                      return { ...prev, habitFocusArea: updated };
                    });
                  }}
                />
                <span className="ml-2">{area}</span>
              </label>
            ))}
          </div>

          <div id="aboutMe">
            <input
              type="text"
              name="aboutMe"
              placeholder="Add something about the personality about this impact type"
              value={formData.aboutMe}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div id="pic">
            <input
              type="file"
              name="pic"
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

         <div id="impacts" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic
              name={"Create"}
              type={"create"}
              handleClick={handleSubmit}
            />
            <EtBtnFormBasic
              name={"Clear"}
              type={"clear"}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={"Cancel"}
              type={"cancel"}
              handleClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  </div>
)
}

export default EtCreateHabit